import { CnSponsorForm } from '../components/CnSponsorForm'
import { usePageMeta } from '../lib/meta'
import { cnyAnnual, payUrl } from '../lib/stripe'

// Chinese-only sponsorship landing for Chinese freight forwarders. Copy is written in natural
// business Chinese (not machine-translated) and positions placement as lead-gen, not vanity ads.
export function CnPartnersPage() {
  usePageMeta(
    'navo24 货代合作招募 — 把您的航线放到英国、欧洲、美国买家面前',
    'navo24 是英国注册的货运平台，服务英国、欧洲、美国的进口商。上架您经营的中国出口航线，让正在比价下单的海外买家主动找到您，询盘直接转给您。合作套餐 ¥1,980 起。',
    '/cn'
  )

  return (
    <div className="mx-auto max-w-3xl px-5 pt-14 pb-16">
      {/* Hero */}
      <div className="text-center text-sm font-semibold tracking-wide text-signal">navo24 · 英国货运平台</div>
      <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight text-center">
        您在开船，
        <br />
        让海外买家来找您。
      </h1>
      <p className="mt-4 text-center text-muted leading-relaxed">
        navo24 是一家<strong className="text-ink">英国注册</strong>
        的货运平台，服务英国、欧洲和美国的进口商。他们每天在平台上比较中国出口航线的船期、运价和承运人——
        我们把合作伙伴，直接展示给正在搜索这条航线的买家。
      </p>
      <p className="mt-2 text-center text-sm text-muted leading-relaxed">
        这不是广告位，是一条获客渠道：船是您在开，我们把需要这条航线的海外进口商送到您面前，成单归您。
      </p>

      {/* Value */}
      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        {[
          {
            h: '精准买家',
            p: '正在查询中国出口船期与运价的英国、欧洲、美国进口商与货代，意向明确。',
          },
          {
            h: '航线曝光',
            p: '在您经营的航线页面置顶展示公司名称、Logo 与联系方式。',
          },
          {
            h: '询盘直达',
            p: '相关航线的买家询盘，通过微信或邮箱直接转给您，每月附获客报告。',
          },
        ].map((c) => (
          <div key={c.h} className="rounded-lg border border-border-strong bg-surface-0 p-5">
            <div className="font-display font-bold text-signal">{c.h}</div>
            <p className="mt-2 text-sm text-muted leading-relaxed">{c.p}</p>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <h2 className="mt-14 font-display text-2xl font-bold text-center">合作套餐</h2>
      <p className="mt-1 text-center text-sm text-muted">
        人民币计价，可随时取消。每条航线的首批合作伙伴享创始价。
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          {
            key: 'base' as const,
            name: '基础 · 入驻',
            price: '¥1,980',
            year: '年付 ¥19,800（省两个月）',
            feats: ['认证企业主页', '展示 3 条航线', '一键联系按钮', '基础获客月报'],
            featured: false,
          },
          {
            key: 'featured' as const,
            name: '高级 · 推荐',
            price: '¥4,980',
            year: '年付 ¥49,800（省两个月）',
            feats: [
              '含基础全部',
              '8 条航线置顶',
              '航线页 Logo 展示',
              '买家询盘直转',
              '比价表优先',
              '详细获客月报',
            ],
            featured: true,
          },
          {
            key: 'flagship' as const,
            name: '旗舰 · 独家',
            price: '¥8,800',
            year: '年付 ¥88,000（省两个月）',
            feats: [
              '含高级全部',
              '单条航线独家标识',
              '首页与邮件推荐',
              '专属落地页',
              '指定航线全量询盘',
              '专属客户经理',
            ],
            featured: false,
          },
        ].map((t) => (
          <div
            key={t.name}
            className={`relative flex flex-col rounded-lg border bg-surface-0 p-5 ${
              t.featured ? 'border-signal' : 'border-border-strong'
            }`}
          >
            {t.featured && (
              <div className="absolute -top-3 right-4 rounded-full bg-signal px-3 py-1 text-xs font-bold text-on-accent">
                最受欢迎
              </div>
            )}
            <div className="font-display text-lg font-bold">{t.name}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-3xl font-extrabold">{t.price}</span>
              <span className="text-sm text-muted">/ 月</span>
            </div>
            <div className="mt-1 text-xs text-signal">{t.year}</div>
            <ul className="mt-4 flex flex-col gap-2">
              {t.feats.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-muted">
                  <span className="text-signal">→</span>
                  {f}
                </li>
              ))}
            </ul>
            <a
              href={payUrl(t.key) || '#signup'}
              {...(payUrl(t.key) ? { target: '_blank', rel: 'noreferrer' } : {})}
              className={`mt-5 block rounded-md px-4 py-2.5 text-center text-sm font-semibold ${
                t.featured
                  ? 'bg-signal text-on-accent hover:opacity-90'
                  : 'border border-signal text-signal hover:bg-signal/10'
              }`}
            >
              {payUrl(t.key) ? `年付 ${cnyAnnual(t.key)} · 立即支付` : '立即开通'}
            </a>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-xs text-muted">
        支持微信支付 · 支付宝 · 银行卡 —— 一次性年付，费用直接结算给 navo24（英国）。
      </p>

      {/* Trust */}
      <div className="mt-14 rounded-lg border border-border-strong bg-surface-0 p-6">
        <h2 className="font-display text-xl font-bold">为什么信任 navo24</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            { h: '英国注册平台', p: '面向英国、欧洲、美国进口商的正规货运平台。' },
            { h: '中文对接', p: '微信中文沟通，当天回复，省去语言与时差的麻烦。' },
            { h: '真实数据', p: '实时船期与运价——买家真正在用的平台，不是空壳广告。' },
            { h: '按效果合作', p: '以获客为目标，先看询盘再谈续费，风险由我们承担。' },
          ].map((c) => (
            <div key={c.h}>
              <div className="font-display font-bold text-signal">{c.h}</div>
              <p className="mt-1 text-sm text-muted leading-relaxed">{c.p}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA form */}
      <div id="signup" className="mt-10 rounded-lg border border-signal/40 bg-surface-0 p-6">
        <h2 className="font-display text-xl font-bold">留下合作意向</h2>
        <p className="mt-1 text-sm text-muted">
          告诉我们您经营的航线，我们会通过微信或邮件与您联系，为您安排展位。
        </p>
        <div className="mt-4">
          <CnSponsorForm />
        </div>
        <p className="mt-4 text-xs text-muted">
          也可直接联系：微信 <strong className="text-ink">[您的微信号]</strong> · 邮箱{' '}
          <strong className="text-ink">partners@navo24.com</strong>
        </p>
      </div>
    </div>
  )
}
